import * as Cesium from '@cesiumgs/cesium-analytics'
import type { Cartesian3, ClassificationType, Entity, Viewer } from '@cesiumgs/cesium-analytics'
import {
  Cartesian2,
  Math as CesiumMath,
  Color,
  DistanceDisplayCondition,
  GridMaterialProperty,
  HeadingPitchRoll,
  Matrix3,
  Matrix4,
  Quaternion,
  RectangularSensorGraphics,
  Transforms,
} from '@cesiumgs/cesium-analytics'

export interface VisualAnalysisProps {
  id: string
  name: string
  lineName: string
  basePoint: Cartesian3
  heading: number
  pitch: number
  roll: number
  radius: number
  view: Viewer
  xHalfAngle: number
  yHalfAngle: number
  classificationType: ClassificationType
  billbordPos: Cartesian3
  visibleColor?: Color
  occludedColor?: Color
}
/**
 *
 * Sensor
 * @export
 * @class VisualAnalysis
 * @example
 *   new VisualAnalysis({
            view: viewer,
            xHalfAngle: 25,
            yHalfAngle: 30,
            heading: 0,
            pitch: 0,
            roll: 0,
            classificationType: ClassificationType.BOTH,
            radius: 100,
            basePoint: p1 //Cartesian3.fromRadians(res[0].longitude,res[0].latitude,res[0].height)
          })
 */
export default class VisualAnalysis {
  config: VisualAnalysisProps
  viewObj: Entity
  constructor(props: VisualAnalysisProps) {
    if (!props.visibleColor) {
      props.visibleColor = Color.LIME.withAlpha(0.5)
    }
    if (!props.visibleColor) {
      props.visibleColor = Color.RED.withAlpha(0.5)
    }
    this.config = props
    const { id, name, lineName, heading, pitch, roll, basePoint, view, radius, xHalfAngle, yHalfAngle, classificationType, billbordPos } = this.config
    const headingPitchRoll = new HeadingPitchRoll(CesiumMath.toRadians(heading), CesiumMath.toRadians(pitch), CesiumMath.toRadians(roll))
    const modelMatrix = Transforms.headingPitchRollToFixedFrame(basePoint, headingPitchRoll)
    const rotation = Matrix4.getMatrix3(modelMatrix, new Matrix3())
    const orientation: Quaternion = Quaternion.fromRotationMatrix(rotation)
    const lateralSurfaceMaterial = new GridMaterialProperty({
      color: new Color(0, 1, 0, 1.0),
      cellAlpha: 0,
      lineCount: new Cartesian2(5, 5),
    })

    const domeSurfaceMaterial = new GridMaterialProperty({
      color: new Color(0, 1, 0, 1.0),
      cellAlpha: 0,
      lineCount: new Cartesian2(5, 5),
    })

    const environmentOcclusionMaterial = new GridMaterialProperty({
      color: Color.fromAlpha(Color.WHITE, 0.1), // new Color(1.0, 0.0, 0.0, 1.0),
      cellAlpha: 0.5,
      lineCount: new Cartesian2(5, 5),
    })

    this.viewObj = view.entities.add({
      id: `${name}`,
      position: basePoint,
      orientation: Transforms.headingPitchRollQuaternion(basePoint, headingPitchRoll) as any,
      rectangularSensor: new RectangularSensorGraphics({
        radius,
        xHalfAngle: CesiumMath.toRadians(xHalfAngle),
        yHalfAngle: CesiumMath.toRadians(yHalfAngle),
        lateralSurfaceMaterial,
        domeSurfaceMaterial,
        environmentOcclusionMaterial,
        showLateralSurfaces: true,
        showEllipsoidHorizonSurfaces: false,
        showDomeSurfaces: true,
        showEllipsoidSurfaces: false,
        environmentConstraint: true,
        showEnvironmentOcclusion: true,
        showViewshed: true,
        classificationType,
        showIntersection: true,
        showThroughEllipsoid: false,
        viewshedVisibleColor: this.config.visibleColor,
        viewshedOccludedColor: this.config.occludedColor, // Color.fromAlpha(Color.WHITE, 0.1),
      }),
    })
    const cartographic = Cesium.Cartographic.fromCartesian(basePoint)
    view.camera.flyTo({
      destination: Cesium.Cartesian3.fromDegrees(Cesium.Math.toDegrees(cartographic.longitude), Cesium.Math.toDegrees(cartographic.latitude), 2500),
      orientation: {
        heading: Cesium.Math.toRadians(0),
      },
      duration: 3,
    })
    if (billbordPos) {
      view.entities.add({
        id: `${id}`,
        position: basePoint,
        label: {
          text: `${id}`,
          font: '16px sans-serif',
          style: Cesium.LabelStyle.FILL,
          fillColor: Color.fromCssColorString('#e7b608'),
          outlineWidth: 1,
          outlineColor: Color.fromCssColorString('#ffffff'),
          verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
          pixelOffset: new Cesium.Cartesian2(0, -20),
          horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
          distanceDisplayCondition: new DistanceDisplayCondition(0, 200000),
        },
        billboard: {
          image: './images/shexiangto.png',
          show: true, // default
          pixelOffset: new Cartesian2(0, 0), // default: (0, 0)
          // eyeOffset: new Cartesian3(0.0, 0.0, 0.0), // default
          // horizontalOrigin: HorizontalOrigin.CENTER, // default
          // verticalOrigin: VerticalOrigin.BOTTOM, // default: CENTER

          width: 40, // default: undefined
          height: 40, // default: undefined
        },
      })
      view.entities.add({
        id: `${lineName}`,
        polyline: {
          positions: [billbordPos, basePoint],
          width: 2,
          show: true, // default
          material: Color.YELLOW,
        },
      })
    }
  }

  setHeadingPitchRoll(heading: any, pitch: any, roll: any) {
    const { view, basePoint } = this.config
    const headingPitchRoll = new HeadingPitchRoll(CesiumMath.toRadians(heading), pitch, CesiumMath.toRadians(roll))
    const modelMatrix = Transforms.headingPitchRollToFixedFrame(basePoint, headingPitchRoll)
    const rotation = Matrix4.getMatrix3(modelMatrix, new Matrix3())
    const orientation: Quaternion = Quaternion.fromRotationMatrix(rotation)

    const entity: any = this.viewObj
    if (entity) {
      entity.orientation = orientation
      // view.scene.requestRender()
    }
  }

  setVertical(value: any) {
    const { view } = this.config
    const entity: any = this.viewObj
    if (entity) {
      entity.rectangularSensor.yHalfAngle = CesiumMath.toRadians(value)
      // view.scene.requestRender()
    }
  }

  setHorizontal(value: any) {
    const { view } = this.config
    const entity: any = this.viewObj
    if (entity) {
      entity.rectangularSensor.xHalfAngle = CesiumMath.toRadians(value)
      // view.scene.requestRender()
    }
  }

  setDistance(value: any) {
    const { view } = this.config
    const entity: any = this.viewObj
    if (entity) {
      entity.rectangularSensor.radius = value
      // view.scene.requestRender()
    }
  }

  setPositionAlt(value: any) {
    const { view } = this.config
    const entity = this.viewObj
    if (entity) {
      // let nposition=new Cartesian3( entity.position.getValue().x,entity.position.getValue().y)
      // entity.position.getValue(). = value;
    }
  }

  clear() {
    const { view } = this.config
    view.entities.removeAll()
  }
}
